// context/ChatContext.js - Add game-related state and handlers
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { useAuth } from './AuthContext';
import api from '../services/api';
import notification from "../assets/notification_1.mp3";

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  
  // Chat states
  const [chatOpen, setChatOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [currentChat, setCurrentChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState({
    name: user?.username || '',
    email: user?.email || ''
  });
  
  // Game states
  const [gameInvitations, setGameInvitations] = useState([]);
  const [pendingGameInvitation, setPendingGameInvitation] = useState(null);
  const [activeGame, setActiveGame] = useState(null);
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [gameNotification, setGameNotification] = useState(null);
  
  // Game event handlers (will be set by the game component)
  const gameEventHandlers = useRef({
    onGameStarted: null,
    onGameStateUpdated: null,
    onGameEnded: null,
    onGameInvitation: null,
    onInvitationSent: null,
    onInvitationRejected: null,
    onRematchRequested: null,
    onRematchStarted: null,
    onPlayerDisconnected: null,
    onOnlinePlayers: null
  });
  
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Get session ID from localStorage or generate new one
  const getSessionId = () => {
    let sessionId = localStorage.getItem('chat_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chat_session_id', sessionId);
    }
    return sessionId;
  };

  // Initialize socket connection
  useEffect(() => {
    const sessionId = getSessionId();
    const token = localStorage.getItem('token');
    
    const socketUrl = process.env.REACT_APP_SOCKET_URL || 'wss://retocafe.com:9041';
  
    console.log('Connecting to socket:', socketUrl, { sessionId, hasToken: !!token });
  
    const newSocket = io(socketUrl, {
      auth: {
        token: token || null,
        sessionId
      },
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      autoConnect: true,
      forceNew: true
    });

    newSocket.on('connect', () => {
      console.log('Socket connected');
      setIsConnected(true);
      // Register table AFTER connection is established
      const tableId = localStorage.getItem('tableId');
      if (tableId) {
        registerTable(tableId);
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      setIsConnected(false);
      
      if (newSocket.io.opts.transports[0] === 'websocket') {
        console.log('Falling back to polling transport');
        newSocket.io.opts.transports = ['polling', 'websocket'];
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
    });

    newSocket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    // ============= CHAT EVENT HANDLERS =============
    newSocket.on('chatStarted', (data) => {
      if (data.success) {
        setCurrentChat(data.chat);
        setMessages(data.chat.messages || []);
        setUnreadCount(0);
      }
    });

    newSocket.on('newMessage', (message) => {
      setMessages(prev => [...prev, message]);
      
      if (!chatOpen || minimized) {
        playNotificationSound();
        setUnreadCount(prev => prev + 1);
      }
      
      setTimeout(scrollToBottom, 100);
    });

    newSocket.on('userTyping', (data) => {
      if (data.role === 'admin') {
        setIsTyping(data.isTyping);
      }
    });

    newSocket.on('chatClosed', () => {
      setCurrentChat(null);
      setMessages([]);
      setChatOpen(false);
    });

    // ============= GAME EVENT HANDLERS =============
    newSocket.on('onlinePlayers', (players) => {
        const currentUserTableId =localStorage.getItem('tableId');
  const currentUserId = user?.id?.toString();
  
  // Filter out:
  // 1. Current player's table
  // 2. Players currently in game
  // 3. Current user (if you want to exclude yourself by user ID)
  const filteredPlayers = players.filter(p => {
    // Exclude current player's table
    if (p.tableId === currentUserTableId) return false;
    
    // Exclude players currently in game
    if (p.isInGame) return false;
    
    // Optional: Exclude current user by ID (if you have user-based filtering)
    if (currentUserId && p.userId === currentUserId) return false;
    
    // Optional: Exclude players who are offline or inactive
    if (p.status === 'offline') return false;
    
    return true;
  });
  
  // Sort filtered players (optional: show VIP tables first)
  const sortedPlayers = filteredPlayers.sort((a, b) => {
    // VIP tables first
    if (a.isVip && !b.isVip) return -1;
    if (!a.isVip && b.isVip) return 1;
    // Then by table number
    return (a.tableNumber || 0) - (b.tableNumber || 0);
  });
  
  // Update local onlinePlayers state
  setOnlinePlayers(sortedPlayers);

      if (gameEventHandlers.current.onOnlinePlayers) {
        gameEventHandlers.current.onOnlinePlayers(players);
      }
    });

    newSocket.on('gameInvitation', (invitation) => {
      setPendingGameInvitation(invitation);
      setGameInvitations(prev => [...prev, invitation]);
      playNotificationSound();
      
      if (gameEventHandlers.current.onGameInvitation) {
        gameEventHandlers.current.onGameInvitation(invitation);
      }
    });

    newSocket.on('gameStarted', (game) => {
      setActiveGame(game);
      setPendingGameInvitation(null);
      
      if (gameEventHandlers.current.onGameStarted) {
        gameEventHandlers.current.onGameStarted(game);
      }
    });

    newSocket.on('gameStateUpdated', (update) => {
      if (gameEventHandlers.current.onGameStateUpdated) {
        gameEventHandlers.current.onGameStateUpdated(update);
      }
    });

    newSocket.on('gameEnded', (result) => {
      setActiveGame(null);
      
      if (gameEventHandlers.current.onGameEnded) {
        gameEventHandlers.current.onGameEnded(result);
      }
    });

    newSocket.on('invitationSent', (data) => {
      if (gameEventHandlers.current.onInvitationSent) {
        gameEventHandlers.current.onInvitationSent(data);
      }
    });

    newSocket.on('invitationRejected', (data) => {
      if (gameEventHandlers.current.onInvitationRejected) {
        gameEventHandlers.current.onInvitationRejected(data);
      }
    });

    newSocket.on('rematchRequested', (data) => {
      if (gameEventHandlers.current.onRematchRequested) {
        gameEventHandlers.current.onRematchRequested(data);
      }
    });

    newSocket.on('rematchStarted', (data) => {
      if (gameEventHandlers.current.onRematchStarted) {
        gameEventHandlers.current.onRematchStarted(data);
      }
    });

    newSocket.on('playerDisconnected', (data) => {
      setActiveGame(null);
      
      if (gameEventHandlers.current.onPlayerDisconnected) {
        gameEventHandlers.current.onPlayerDisconnected(data);
      }
    });

    newSocket.on('playerRegistered', (data) => {
      console.log('Player registered:', data);
    });

    newSocket.on('playerStatusChanged', (data) => {
      console.log('Player status changed:', data);
    });

    newSocket.on('gameError', (error) => {
      console.error('Game error:', error);
      setGameNotification({ message: error.message, severity: 'error' });
      setTimeout(() => setGameNotification(null), 3000);
      
      if (gameEventHandlers.current.onGameError) {
        gameEventHandlers.current.onGameError(error);
      }
    });

    setSocket(newSocket);

    return () => {
      if (newSocket) {
        newSocket.disconnect();
        newSocket.close();
      }
    };
  }, [user]);

  // Load existing chat on mount
  useEffect(() => {
    const loadExistingChat = async () => {
      try {
        const response = await api.get('/chat/my-chat');
        if (response.data) {
          setCurrentChat(response.data);
          setMessages(response.data.messages || []);
        }
      } catch (error) {
        console.error('Error loading chat:', error);
      }
    };
    
    loadExistingChat();
  }, []);

  // Update userInfo when user changes
  useEffect(() => {
    if (user) {
      setUserInfo({
        name: user.username || '',
        email: user.email || ''
      });
    }
  }, [user]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const playNotificationSound = () => {
    try {
      const audio = new Audio(notification);
      audio.play().catch(() => {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        
        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.2);
      });
    } catch (error) {
      console.log('Could not play sound');
    }
  };

  // ============= CHAT FUNCTIONS =============
  const startChat = useCallback(async (name, email, initialMessage = '') => {
    if (!socket || !isConnected) {
      console.error('Socket not connected');
      return false;
    }
    
    return new Promise((resolve, reject) => {
      const handleChatStarted = (data) => {
        if (data.success) {
          setCurrentChat(data.chat);
          setMessages(data.chat.messages || []);
          setUnreadCount(0);
          resolve(true);
        } else {
          reject(new Error('Failed to start chat'));
        }
      };

      const handleChatError = (error) => {
        reject(new Error(error.message || 'Chat error'));
      };

      socket.once('chatStarted', handleChatStarted);
      socket.once('chatError', handleChatError);

      socket.emit('startChat', {
        name: name || userInfo.name,
        email: email || userInfo.email,
        message: initialMessage
      });

      setTimeout(() => {
        socket.off('chatStarted', handleChatStarted);
        socket.off('chatError', handleChatError);
        reject(new Error('Chat start timeout'));
      }, 10000);
    });
  }, [socket, isConnected, userInfo]);

    const registerTable = (tableIdValue) => {
    if (isConnected) {
      console.log(`Registering table ${tableIdValue} with socket`);
      socket.emit('registerTable', { tableId: tableIdValue });
      
      // Also send player ready status
      socket.emit('playerReady', {
        tableId: tableIdValue,
        playerName: user?.username || `Table ${tableIdValue}`,
        isReady: true,
        isVip: false // or get from your table data
      });
      
      // Get online players after registration
      socket.emit('getOnlinePlayers');
    } else {
      console.log('Socket not connected yet, will register when connected');
      // Wait for connection and try again
      const checkConnection = setInterval(() => {
        if (isConnected) {
          socket.emit('registerTable', { tableId: tableIdValue });
          clearInterval(checkConnection);
        }
      }, 500);
      
      // Timeout after 5 seconds
      setTimeout(() => clearInterval(checkConnection), 5000);
    }
  };


  const sendMessage = (message) => {
    if (!socket || !isConnected || !currentChat) {
      console.error('Cannot send message');
      return false;
    }
    
    if (!message.trim()) return false;
    
    socket.emit('sendMessage', {
      chatId: currentChat.id,
      message: message.trim()
    });
    
    return true;
  };

  const sendTyping = (typing) => {
    if (!socket || !isConnected || !currentChat) return;
    
    socket.emit('typing', {
      chatId: currentChat.id,
      isTyping: typing
    });
    
    if (typing) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit('typing', {
          chatId: currentChat.id,
          isTyping: false
        });
      }, 2000);
    }
  };

  const markAsRead = () => {
    if (!socket || !isConnected || !currentChat) return;
    
    socket.emit('markAsRead', {
      chatId: currentChat.id
    });
    
    setUnreadCount(0);
  };

  const closeChat = () => {
    if (!socket || !isConnected || !currentChat) return;
    
    socket.emit('closeChat', {
      chatId: currentChat.id
    });
    
    setCurrentChat(null);
    setMessages([]);
    setChatOpen(false);
  };

  const toggleChat = () => {
    setChatOpen(prev => !prev);
    setMinimized(false);
    if (!chatOpen) {
      markAsRead();
    }
  };

  const toggleMinimize = () => {
    setMinimized(prev => !prev);
  };

  // ============= GAME FUNCTIONS =============
  const registerGameEventHandlers = (handlers) => {
    gameEventHandlers.current = { ...gameEventHandlers.current, ...handlers };
  };

  const unregisterGameEventHandlers = () => {
    gameEventHandlers.current = {
      onGameStarted: null,
      onGameStateUpdated: null,
      onGameEnded: null,
      onGameInvitation: null,
      onInvitationSent: null,
      onInvitationRejected: null,
      onRematchRequested: null,
      onRematchStarted: null,
      onPlayerDisconnected: null,
      onOnlinePlayers: null,
      onGameError: null
    };
  };

  const sendGameInvitation = (targetTableId, inviterTableId, inviterName) => {
    if (!socket || !isConnected) {
      console.error('Socket not connected');
      return false;
    }
    
    socket.emit('inviteToGame', {
      targetTableId,
      inviterTableId,
      inviterName
    });
    return true;
  };

  const acceptGameInvitation = (gameId, fromTableId, playerTableId, playerName) => {
    if (!socket || !isConnected) {
      console.error('Socket not connected');
      return false;
    }
    
    socket.emit('acceptGameInvitation', {
      gameId,
      targetTableId: fromTableId,
      playerTableId,
      playerName
    });
    return true;
  };

  const rejectGameInvitation = (gameId, playerTableId) => {
    if (!socket || !isConnected) {
      console.error('Socket not connected');
      return false;
    }
    
    socket.emit('rejectGameInvitation', {
      gameId,
      playerTableId
    });
    return true;
  };

  const sendGameMove = (gameId, position, playerId) => {
    if (!socket || !isConnected) {
      console.error('Socket not connected');
      return false;
    }
    
    socket.emit('gameMove', {
      gameId,
      position,
      playerId
    });
    return true;
  };

  const requestGameRematch = (gameId, playerId) => {
    if (!socket || !isConnected) {
      console.error('Socket not connected');
      return false;
    }
    
    socket.emit('requestRematch', {
      gameId,
      playerId
    });
    return true;
  };

  const getOnlinePlayers = () => {
    if (!socket || !isConnected) {
      console.error('Socket not connected');
      return;
    }
    
    socket.emit('getOnlinePlayers');
  };

  const registerPlayerReady = (tableId, playerName) => {
    if (!socket || !isConnected) {
      console.error('Socket not connected');
      return false;
    }
    
    socket.emit('playerReady', {
      tableId,
      playerName,
      isReady: true
    });
    return true;
  };

  const clearPendingInvitation = () => {
    setPendingGameInvitation(null);
  };

  const clearGameNotification = () => {
    setGameNotification(null);
  };

  const addAIMessage = useCallback((message) => {
  if (!currentChat) return;
  
  const aiMessage = {
    id: Date.now(),
    chat_id: currentChat.id,
    sender_id: null,
    sender_type: 'ai',
    message: message,
    is_read: true,
    created_at: new Date().toISOString()
  };
  
  setMessages(prev => [...prev, aiMessage]);
}, [currentChat]);

  return (
    <ChatContext.Provider
      value={{
        // Socket
        socket,
        isConnected,
        
        // Chat
        chatOpen,
        minimized,
        currentChat,
        messages,
        unreadCount,
        isTyping,
        loading,
        userInfo,
        messagesEndRef,
        setUserInfo,
        startChat,
        sendMessage,
        sendTyping,
        markAsRead,
        closeChat,
        toggleChat,
        toggleMinimize,
        setChatOpen,
        
        // Game
        gameInvitations,
        pendingGameInvitation,
        activeGame,
        onlinePlayers,
        gameNotification,
        registerGameEventHandlers,
        unregisterGameEventHandlers,
        sendGameInvitation,
        acceptGameInvitation,
        rejectGameInvitation,
        sendGameMove,
        requestGameRematch,
        getOnlinePlayers,
        registerPlayerReady,
        clearPendingInvitation,
        clearGameNotification,
        setOnlinePlayers,
        registerTable,
        setMessages,
        addAIMessage
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);